import Tooltip from '../common/Tooltip';
import plusButton from '../../images/icons/plus-icon.png';
import deleteIcon from '../../images/icons/delete-icon.png';
import IconButton from '../common/IconButton';

const DATE_OPTIONS = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };

/**
 * De losse antwoorden op één vraag, als bewerkbare kaarten.
 *
 * Twee schermen tonen deze lijst — alle responses op een vraag, en de responses
 * van één deelnemer. Ze verschillen alleen in welke antwoorden ze ophalen, dus
 * de kaart, de knoppen en de rechtencontrole staan hier één keer.
 * AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook
 *
 * @param {object} props
 * @param {Array<object>} props.results - De antwoorden, al in weergavevolgorde.
 * @param {boolean} props.loaded - Of de antwoorden binnen zijn. Zonder dit is
 *   'leeg' niet te onderscheiden van 'nog onderweg', en dan staat er een
 *   bewering op het scherm die niet waar hoeft te zijn.
 *   AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
 * @param {boolean} props.canCrud - Of de gebruiker antwoorden mag toevoegen, wijzigen en verwijderen.
 * @param {boolean} [props.canDelete] - Of de gebruiker antwoorden mag verwijderen (naar de
 *   prullenbak). Los van `canCrud`: een organisatie mag haar eigen antwoorden
 *   verwijderen maar niet wijzigen. Standaard volgt hij `canCrud`.
 * @param {boolean} [props.canAdd] - Of er een antwoord bíj mag. Bij een matrixvraag
 *   niet: een handmatig toegevoegd antwoord hoort dan bij geen enkele stelling
 *   (inbox #190). Standaard volgt hij `canCrud`, zodat een aanroeper die dit niet
 *   meegeeft de knop houdt die hij had.
 * @param {(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void} props.onInput - Slaat een gewijzigd antwoord op.
 * @param {(event: React.MouseEvent<HTMLImageElement>) => void} props.onDelete - Verwijdert één antwoord.
 * @param {() => void} props.onAdd - Voegt een leeg antwoord toe.
 */
const ResearchResponseCards = ({ results, loaded, canCrud, canAdd, canDelete, onInput, onDelete, onAdd }) => {
    const rows = Array.isArray(results) ? results : [];
    const mayDelete = canDelete === undefined ? canCrud : canDelete;
    const mayAdd = canCrud && (canAdd === undefined ? true : canAdd);

    return (
        <div className="research-results-form">
            <div className="list-top-row-container">
                {mayAdd && (
                    <Tooltip content="Respons toevoegen" top="30px" width={'30px'}>
                        <IconButton icon={plusButton} label="Respons toevoegen" onClick={onAdd} />
                    </Tooltip>
                )}
            </div>

            {/* Drie takken op zekerheid: eerst 'weet ik niet', dan 'leeg', dan de
                inhoud. Een leeg vlak liet de lezer raden of er niets was, of het
                nog laadde, of hij iets fout deed. */}
            {rows.length === 0 && (
                <span className="rrf-empty">
                    {loaded ? 'Geen antwoorden op deze vraag voor dit meetmoment.' : 'Antwoorden worden opgehaald…'}
                </span>
            )}

            {rows.map((result, i) => (
                <div key={result.ID || result.docid} className="rrf-card rrf-response-card">
                    <div className="rrf-card-head">
                        <div>
                            <div className="rrf-q-label">
                                #{i + 1} · {result.Timestamp?.toDate ? result.Timestamp.toDate().toLocaleDateString('nl-NL', DATE_OPTIONS) : ''}
                            </div>
                            {(result.FieldType === 'matrix-one' || result.FieldType === 'matrix-multiple') && (
                                <h3>{result.RowTitle}{result.RowTitleRight ? ` ↔ ${result.RowTitleRight}` : ''}</h3>
                            )}
                        </div>
                        {mayDelete && (
                            <Tooltip content="Verwijder deze response" top="30px" width={'30px'}>
                                {/* De titels gaan mee zodat de prullenbak kan tonen WAT er weg is
                                    in plaats van een document-id.
                                    AI-CONTEXT: See docs/pitfalls.md#state-event-target-vs-currenttarget */}
                                <img
                                    className="rrf-response-delete"
                                    src={deleteIcon}
                                    alt="Verwijder deze response"
                                    data-docid={result.docid}
                                    data-rowtitle={result.RowTitle || ''}
                                    data-fieldtitle={result.FieldTitle || ''}
                                    onClick={onDelete}
                                />
                            </Tooltip>
                        )}
                    </div>
                    <div className="rrf-response-edit">
                        {result.FieldType === 'matrix-one' || result.FieldType === 'matrix-multiple' ? (
                            <input defaultValue={result.Input} data-docid={result.docid} onChange={onInput} readOnly={!canCrud} />
                        ) : (
                            <textarea rows="2" defaultValue={result.Input} data-docid={result.docid} onChange={onInput} readOnly={!canCrud} />
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ResearchResponseCards;
